import React from "react";
import { Button, Callout } from "./components/DesignSystem";
import type { AuthSession, PlannerModuleKey, PlannerWorkspace, Scope } from "./domain/types";
import { AdminScreen } from "./screens/AdminScreen";
import { AuthScreens } from "./screens/AuthScreens";
import { EventsScreen } from "./screens/EventsScreen";
import { CalendarScreen } from "./screens/CalendarScreen";
import { DayOfScreen } from "./screens/DayOfScreen";
import { RunSheetsScreen } from "./screens/RunSheetsScreen";
import { GuestsScreen, HouseholdsScreen, RsvpScreen } from "./screens/GuestsScreens";
import { HomeScreen } from "./screens/HomeScreen";
import { ImportGuestsScreen } from "./screens/ImportGuestsScreen";
import { InvitationsScreen } from "./screens/InvitationsScreen";
import { MenuScreen } from "./screens/MenuScreen";
import { MoodboardsScreen } from "./screens/MoodboardsScreen";
import { OutfitsScreen } from "./screens/OutfitsScreen";
import { BeautyScreen } from "./screens/BeautyScreen";
import { PhotographyScreen } from "./screens/PhotographyScreen";
import { GiftsScreen } from "./screens/GiftsScreen";
import { GiftsReceivedScreen } from "./screens/GiftsReceivedScreen";
import { TravelScreen } from "./screens/TravelScreen";
import { TransportScreen } from "./screens/TransportScreen";
import { OnboardingScreen } from "./screens/OnboardingScreen";
import { InvitationBriefScreen } from "./screens/InvitationBriefScreen";
import { LockedModuleScreen } from "./screens/LockedModuleScreen";
import { CollaboratorInviteScreen } from "./screens/CollaboratorInviteScreen";
import { CollaboratorsScreen } from "./screens/CollaboratorsScreen";
import { PublicRsvpScreen } from "./screens/PublicRsvpScreen";
import { PublicWebsiteUnavailable } from "./screens/PublicWebsiteUnavailable";
import { publicWebsitePath, resolveLocation, routePaths, type LocationRoute, type PlannerRoute } from "./domain/routing";
import { SeatingScreen } from "./screens/SeatingScreen";
import { SecurityScreen } from "./screens/SecurityScreen";
import { StationeryScreen } from "./screens/StationeryScreen";
import { DocumentsScreen, UpdatesScreen } from "./screens/UpdatesDocumentsScreens";
import { BudgetScreen, PaymentsScreen, TasksScreen, VendorsScreen, VenuesScreen } from "./screens/Wave2Screens";
import { LoadingState } from "./screens/common";
import { isAdminSession, MobileMore, PlannerShell, type RouteKey } from "./shell/PlannerShell";
import { usePlannerData } from "./state/PlannerDataContext";

const PublicWebsiteScreen = React.lazy(() => import("./screens/PublicWebsiteScreen").then((module) => ({ default: module.PublicWebsiteScreen })));
// Website Builder V2 (direct-manipulation editor): the default experience for every role since
// Phase 5A of effervescent-napping-robin.md. The old three-column WebsiteScreen builder (and its
// admin-only ?legacyWebsiteBuilder=true fallback) was removed in Phase 5B once this cleared the
// acceptance test on desktop and mobile.
const WebsiteEditor = React.lazy(() => import("./screens/website/WebsiteEditor").then((module) => ({ default: module.WebsiteEditor })));

const routeModules: Partial<Record<RouteKey, PlannerModuleKey>> = {
  events: "events",
  tasks: "tasks",
  calendar: "calendar",
  runsheets: "runsheets",
  dayof: "dayof",
  budget: "budget",
  payments: "payments",
  vendors: "vendors",
  venues: "venues",
  guests: "guests",
  households: "households",
  rsvp: "rsvp",
  invitations: "invitations",
  seating: "seating",
  stationery: "stationery",
  website: "website",
  moodboards: "moodboards",
  outfits: "outfits",
  beauty: "beauty",
  photography: "photography",
  gifts: "gifts",
  giftsreceived: "gifts_received",
  menu: "menu",
  travel: "travel",
  transport: "transport",
  import: "guest_import",
  documents: "documents",
  collaborators: "collaborators"
};

function parseLocation(): LocationRoute {
  return resolveLocation(window.location.hostname, window.location.pathname, window.location.search);
}

function isRouteKey(value: string): value is RouteKey {
  return value in routePaths;
}

function routeAllowed(route: RouteKey, workspace: PlannerWorkspace | null, session: AuthSession | null): boolean {
  if (route === "admin" || route === "onboarding") return isAdminSession(session);
  const module = routeModules[route];
  return !module || Boolean(workspace?.wedding.enabledModules.includes(module));
}

export function App() {
  const { session, workspace, loading, error, service } = usePlannerData();
  const [locationRoute, setLocationRoute] = React.useState<LocationRoute>(() => parseLocation());
  const [scope, setScope] = React.useState<Scope>("all");
  const [forcePlanner, setForcePlanner] = React.useState(false);

  React.useEffect(() => {
    const onPop = () => setLocationRoute(parseLocation());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // An old "/w/<slug>" link opened on the weddings.pocketsofbeige.com guest domain still renders
  // the site immediately (no round trip), but the address bar is straightened out to the domain's
  // real canonical path -- replaceState only, so it neither adds a history entry nor re-triggers
  // this same route.
  React.useEffect(() => {
    if (locationRoute.kind === "public-website" && locationRoute.canonicalPath) {
      window.history.replaceState(null, "", locationRoute.canonicalPath);
    }
  }, [locationRoute]);

  // Feeds the Admin "account activity" report (login times / time-on-app per account) -- a ping
  // every 30s while this account is signed in AND the tab is visible, so a backgrounded/closed tab
  // doesn't count as time spent. Fires immediately on sign-in/tab-refocus too, not just on the
  // interval, so a very short visit still registers as one heartbeat rather than zero.
  React.useEffect(() => {
    if (!session) return;
    const HEARTBEAT_INTERVAL_MS = 30_000;
    const sendHeartbeat = () => {
      if (document.visibilityState === "visible") void service.analytics.heartbeat();
    };
    sendHeartbeat();
    const interval = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    document.addEventListener("visibilitychange", sendHeartbeat);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", sendHeartbeat);
    };
  }, [session, service]);

  const route: PlannerRoute = locationRoute.kind === "planner" ? locationRoute.route : "home";
  const activeRoute: RouteKey = route === "not-found" ? "home" : route;

  const go = (next: string) => {
    if (!isRouteKey(next)) {
      const path = `/missing/${encodeURIComponent(next)}`;
      window.history.pushState(null, "", `${path}${window.location.search}`);
      setLocationRoute({ kind: "planner", route: "not-found", attemptedPath: path });
      return;
    }
    window.history.pushState(null, "", `${routePaths[next]}${window.location.search}`);
    setLocationRoute({ kind: "planner", route: next });
  };

  if (locationRoute.kind === "public-rsvp") return <PublicRsvpScreen token={locationRoute.token} />;
  // Fully public, no session/onboarding gates at all -- a guest hitting a bare path on the
  // weddings.pocketsofbeige.com domain that never matched a slug shape sees this domain's own
  // not-found state, never the planner's "unknown route" or activation screens.
  if (locationRoute.kind === "public-website-not-found") return <PublicWebsiteUnavailable />;
  // Fully public, no session/onboarding gates at all -- an anonymous guest visiting a wedding's
  // site must never be routed into sign-in or onboarding.
  if (locationRoute.kind === "public-website") {
    return (
      <React.Suspense fallback={<div className="pob-app" style={{ display: "grid", placeItems: "center" }}><LoadingState label="Opening wedding website" /></div>}>
        <PublicWebsiteScreen
          slug={locationRoute.slug}
          token={locationRoute.token}
          onRedirect={(nextSlug) => {
            const params = new URLSearchParams(window.location.search);
            const suffix = params.toString() ? `?${params}` : "";
            window.history.replaceState(null, "", `${publicWebsitePath(window.location.hostname, nextSlug)}${suffix}`);
            setLocationRoute({ kind: "public-website", slug: nextSlug, token: params.get("rsvp") || undefined });
          }}
        />
      </React.Suspense>
    );
  }
  // Rendered before the session/onboarding gates below (like public-rsvp) so accepting an invite
  // never gets routed into "create a new wedding" onboarding -- the screen handles its own
  // sign-in-if-needed step and re-renders once a session appears.
  if (locationRoute.kind === "collaborator-invite") return <CollaboratorInviteScreen token={locationRoute.token} />;
  if (loading && !workspace) return <div className="pob-app" style={{ display: "grid", placeItems: "center" }}><LoadingState label="Opening planner" /></div>;
  if (error && !workspace) return <div className="pob-pad"><Callout tone="error" title="Application could not start">{error}</Callout></div>;
  if (route === "not-found" && !session) return <UnknownRoute path={locationRoute.kind === "planner" ? locationRoute.attemptedPath : undefined} goHome={() => go("home")} />;
  // A signed-in visitor opening their own old activation link just lands on their planner (via
  // the route-derivation fallback to "home" above) rather than being pulled back into activation
  // -- this path only matters pre-auth, so it's checked alongside the other !session gates below.
  if (locationRoute.kind === "activation-link" && !session) {
    return <AuthScreens initialView="activate" presetOrderNumber={locationRoute.orderNumber} onAuthenticated={() => setForcePlanner(false)} />;
  }
  if (!session) return <AuthScreens onAuthenticated={() => setForcePlanner(false)} />;
  if (!session.verified) return <AuthScreens onAuthenticated={() => setForcePlanner(false)} />;
  if (!session.onboardingComplete && !forcePlanner) return <OnboardingScreen onComplete={() => { setForcePlanner(true); go("home"); }} />;

  return (
    <InvitationBriefGate>
      <PlannerShell route={activeRoute} setRoute={go} scope={scope} setScope={setScope}>
        {route === "not-found"
          ? <UnknownRoute path={locationRoute.kind === "planner" ? locationRoute.attemptedPath : undefined} goHome={() => go("home")} />
          : !routeAllowed(route, workspace, session)
            ? routeModules[route]
              ? <LockedModuleScreen moduleKey={routeModules[route]!} />
              : <ModuleUnavailable route={route} goHome={() => go("home")} />
            : <RouteView route={route} scope={scope} setScope={setScope} go={go} />}
      </PlannerShell>
    </InvitationBriefGate>
  );
}

// One-time gate (Wave 4.5 §1): a wedding account that hasn't submitted an Invitation Brief sees it
// before the normal Home dashboard, the same way OnboardingScreen gates first-run setup above.
// Once submitted, the brief's own status makes this permanently transparent on every future load
// (no flag to reset); "I'll do this later" is the only case needing a persisted skip, since the
// brief otherwise stays DRAFT forever and would re-gate on every reload.
function InvitationBriefGate({ children }: { children: React.ReactNode }) {
  const { service, workspace, refresh } = usePlannerData();
  const [state, setState] = React.useState<"checking" | "show" | "hide">("checking");
  const weddingId = workspace?.wedding.id;

  React.useEffect(() => {
    if (!weddingId) return;
    let cancelled = false;
    const dismissed = window.localStorage.getItem(`pob-invitation-brief-dismissed-${weddingId}`) === "1";
    if (dismissed) {
      setState("hide");
      return;
    }
    service.invitationBrief.getDraft().then((draft) => {
      if (!cancelled) setState(draft.status === "SUBMITTED" ? "hide" : "show");
    }).catch(() => {
      if (!cancelled) setState("hide");
    });
    return () => { cancelled = true; };
  }, [service, weddingId]);

  if (state === "checking") return <div className="pob-app" style={{ display: "grid", placeItems: "center" }}><LoadingState label="Opening planner" /></div>;
  if (state === "show") {
    return (
      <InvitationBriefScreen
        standalone={false}
        onExitToPlanner={() => { void refresh().then(() => setState("hide")); }}
        onLater={() => {
          if (weddingId) window.localStorage.setItem(`pob-invitation-brief-dismissed-${weddingId}`, "1");
          setState("hide");
        }}
      />
    );
  }
  return <>{children}</>;
}

function RouteView({ route, scope, setScope, go }: { route: RouteKey; scope: Scope; setScope: (scope: Scope) => void; go: (route: string) => void }) {
  const { session } = usePlannerData();
  if (route === "home") return <HomeScreen scope={scope} go={go} />;
  if (route === "events") return <EventsScreen scope={scope} setScope={setScope} go={go} />;
  if (route === "tasks") return <TasksScreen scope={scope} go={go} />;
  if (route === "calendar") return <CalendarScreen scope={scope} />;
  if (route === "runsheets") return <RunSheetsScreen />;
  if (route === "dayof") return <DayOfScreen />;
  if (route === "budget") return <BudgetScreen scope={scope} go={go} />;
  if (route === "payments") return <PaymentsScreen scope={scope} go={go} />;
  if (route === "vendors") return <VendorsScreen scope={scope} go={go} />;
  if (route === "venues") return <VenuesScreen scope={scope} go={go} />;
  if (route === "guests") return <GuestsScreen scope={scope} go={go} />;
  if (route === "households") return <HouseholdsScreen scope={scope} />;
  if (route === "rsvp") return <RsvpScreen scope={scope} />;
  if (route === "invitations") return <InvitationsScreen />;
  if (route === "seating") return <SeatingScreen scope={scope} />;
  if (route === "stationery") return <StationeryScreen />;
  if (route === "website") {
    return (
      <React.Suspense fallback={<LoadingState label="Opening website builder" />}>
        <WebsiteEditor />
      </React.Suspense>
    );
  }
  if (route === "moodboards") return <MoodboardsScreen />;
  if (route === "outfits") return <OutfitsScreen />;
  if (route === "beauty") return <BeautyScreen />;
  if (route === "photography") return <PhotographyScreen />;
  if (route === "gifts") return <GiftsScreen />;
  if (route === "giftsreceived") return <GiftsReceivedScreen />;
  if (route === "menu") return <MenuScreen />;
  if (route === "travel") return <TravelScreen />;
  if (route === "transport") return <TransportScreen />;
  if (route === "import") return <ImportGuestsScreen go={go} />;
  if (route === "updates") return <UpdatesScreen go={go} />;
  if (route === "documents") return <DocumentsScreen />;
  if (route === "security") return <SecurityScreen />;
  if (route === "collaborators") return <CollaboratorsScreen />;
  if (route === "onboarding") return <OnboardingScreen onComplete={() => go("home")} />;
  if (route === "invitation-brief") return <InvitationBriefScreen standalone />;
  if (route === "admin") return <AdminScreen />;
  if (route === "more") return <MobileMore setRoute={go} />;
  return <HomeScreen scope={scope} go={go} />;
}

function UnknownRoute({ path, goHome }: { path?: string; goHome: () => void }) {
  return (
    <div className="pob-pad">
      <Callout tone="error" title="This planner URL does not exist" action={<Button size="sm" onClick={goHome}>Open home</Button>}>
        {path || window.location.pathname} is not a valid Milestone 1 route.
      </Callout>
    </div>
  );
}

function ModuleUnavailable({ route, goHome }: { route: RouteKey; goHome: () => void }) {
  return (
    <div className="pob-pad">
      <Callout tone="warning" title="Module not enabled" action={<Button size="sm" onClick={goHome}>Open home</Button>}>
        This Custom Planner workspace does not include {route}. The backend entitlement gate is authoritative.
      </Callout>
    </div>
  );
}
